/* -------------------------------------------------------------------------
 * Sayuri signature · tea-leaves-drift  (Tea Garden / Yame)
 * -------------------------------------------------------------------------
 * Self-registers with window.SayuriAnimations on load. Renders Tea
 * Garden's full atmosphere — THREE particle layers in one mount. The
 * vibe should read meditative, not dramatic — slower motion than
 * Momiji's maple-fall, narrower color spread, sparser density.
 *
 *   1. TEA LEAVES (primary signature — drifting)
 *      ~16 slender gyokuro-style needle-thin tea leaves drifting on 3
 *      diagonal wind paths. Deep-emerald gradient palette. Slow fall
 *      (28–46s) with a gentle spin. Color is per-leaf gradient pairs
 *      drawn from a 7-stop green progression.
 *
 *   2. STEAM WISPS (atmosphere — rising)
 *      ~8 soft white wisps rising upward from below the viewport — the
 *      ONLY layer where motion goes against gravity. Long lifespan
 *      (40–60s), high blur, low opacity. Reads as rising tea steam in a
 *      garden's first morning hour. Sparse on purpose.
 *
 *   3. BAMBOO SHADOWS (depth — slow lateral drift)
 *      ~6 vertical bamboo silhouettes drifting horizontally across the
 *      viewport very slowly (60–90s) at very low opacity. Suggests a
 *      distant bamboo grove blurring through ground mist. Sits behind
 *      the leaves and steam in z-order.
 *
 * Both leaves and wisps support optional real-photo manifests via
 * window.SAYURI_TEA_GARDEN_ASSETS when populated by the Liquid wrapper
 * (mirrors the SAYURI_MOMIJI_ASSETS pattern). When the manifest is
 * absent every layer falls back to procedural SVG — module ships
 * working before any PNGs are uploaded.
 *
 * Scent character: "Yame in spring — first-flush gyokuro, warm stone, a
 * garden in white blossom." Leaves give the structure, steam reinforces
 * the morning quiet, bamboo extends the world beyond the frame.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';
  var SA = window.SayuriAnimations;
  if (!SA) { return; }

  // ════════════════════════════════════════════════════════════════════
  // Layer 1 — Tea leaves (primary signature)
  // ════════════════════════════════════════════════════════════════════
  //
  // Gyokuro tea leaves are slender and needle-shaped — long oval pointed
  // at both ends, often slightly twisted along their length. Three shape
  // variants give visual variety without straying from the silhouette.
  // Coordinates are in a 16×26 viewBox (taller than wide).
  var TEA_LEAF_SHAPES = [
    // Variant 0 — classic slender oval, slight asymmetric curve.
    'M0,-12 C3,-10 4,-4 3,2 C2,7 1,10 0,12 C-1,10 -2,7 -3,2 C-4,-4 -3,-10 0,-12 Z',
    // Variant 1 — twisted needle, sharper at tip.
    'M0,-13 C2.5,-10 4,-5 3.4,1 C2.6,6 1.5,9 0,12 C-1.5,9 -2.6,6 -3.4,1 C-4,-5 -2.5,-10 0,-13 Z',
    // Variant 2 — rolled leaf with slight hook at base.
    'M0,-12 C2.8,-9 3.6,-4 3.2,2 C2.5,7 0.8,11 -1,12 C-2.2,10 -2.8,6 -3.2,2 C-3.6,-4 -2.8,-9 0,-12 Z'
  ];

  // Deep-to-light emerald progression. Pairs i with i+1 or i+2 form
  // believable gradients (sun on one side of the leaf vs shadow).
  var GREEN_COLORS = [
    '#1F3818', // very dark forest
    '#2C4F22', // deep emerald
    '#3F6B30', // matcha green
    '#5A8C44', // bright green
    '#7DA862', // mid sage
    '#A4C285', // pale jade
    '#C4D6A8'  // light leaf tip
  ];

  function buildTeaLeaves(el, ctx) {
    var intensity = ctx.intensity;
    var n = intensity === 'lush' ? 24 : intensity === 'whisper' ? 10 : 16;

    var assets = (typeof window !== 'undefined') && window.SAYURI_TEA_GARDEN_ASSETS;
    var leafImages = (assets && assets.leaves) || [];

    var rand = ctx.seed('tg-leaves'.charCodeAt(0) * 131 + 13 * 7919);

    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var left = rand() * 100;
      var size = 14 + rand() * 16;       // 14–30px (smaller than maple)
      var delay = -rand() * 50;
      var opacity = 0.5 + rand() * 0.4;  // 0.5–0.9

      // Slow meditative fall. 28–46s vs maple's 16–30s.
      var sizeFactor = (size - 14) / 16;
      var dur = 28 + (1 - sizeFactor) * 14 + rand() * 4;

      var shape = TEA_LEAF_SHAPES[Math.floor(rand() * TEA_LEAF_SHAPES.length)];

      var useGradient = rand() > 0.35;       // slightly more leaves get gradients
      var c1Idx = Math.floor(rand() * GREEN_COLORS.length);
      var drift = 1 + Math.floor(rand() * 2);
      var direction = rand() > 0.5 ? 1 : -1;
      var c2Idx = Math.max(0, Math.min(GREEN_COLORS.length - 1, c1Idx + drift * direction));
      var c1 = GREEN_COLORS[c1Idx];
      var c2 = GREEN_COLORS[c2Idx];
      var gradFlip = rand() > 0.5;

      var fallVariant = i % 3;
      var spinVariant = rand() > 0.5 ? 0 : 1;
      var swayDur = 7 + rand() * 4;       // 7–11s (slower than maple's 5–8)
      var spinDur = 12 + rand() * 10;     // 12–22s (slower than maple's 6–14)

      var p = document.createElement('div');
      p.className = 'sayuri-tg-leaf';
      p.style.position = 'absolute';
      p.style.top = '-40px';
      p.style.left = left + 'vw';
      p.style.width = size + 'px';
      p.style.height = size + 'px';
      p.style.opacity = opacity;
      p.style.animation =
        'tgl-fall-' + fallVariant + ' ' + dur + 's linear ' + delay + 's infinite, ' +
        'tgl-sway ' + swayDur + 's ease-in-out infinite';

      if (leafImages.length > 0) {
        var leafUrl = leafImages[Math.floor(rand() * leafImages.length)];
        p.innerHTML =
          '<img src="' + leafUrl + '" alt="" draggable="false" ' +
          'style="width:100%;height:100%;display:block;object-fit:contain;' +
          'animation: tgl-spin-' + spinVariant + ' ' + spinDur + 's linear infinite;">';
      } else {
        var gradId = 'tgl-grad-' + i;
        var fillAttr = useGradient ? 'url(#' + gradId + ')' : c1;
        var gradientDefs = useGradient
          ? '<defs><linearGradient id="' + gradId + '" x1="-3" ' +
              'y1="' + (gradFlip ? 12 : -13) + '" x2="3" ' +
              'y2="' + (gradFlip ? -13 : 12) + '" ' +
              'gradientUnits="userSpaceOnUse">' +
              '<stop offset="0%" stop-color="' + c1 + '"/>' +
              '<stop offset="100%" stop-color="' + c2 + '"/>' +
            '</linearGradient></defs>'
          : '';

        p.innerHTML =
          '<svg viewBox="-5 -14 10 28" xmlns="http://www.w3.org/2000/svg" ' +
          'style="width:100%;height:100%;display:block;overflow:visible;' +
          'animation: tgl-spin-' + spinVariant + ' ' + spinDur + 's linear infinite;">' +
            gradientDefs +
            '<path d="' + shape + '" fill="' + fillAttr + '" ' +
              'stroke="rgba(20,40,15,0.35)" stroke-width="0.3" ' +
              'stroke-linejoin="miter"/>' +
            // Single midrib vein along the long axis.
            '<line x1="0" y1="-11" x2="0" y2="11" ' +
              'stroke="rgba(20,40,15,0.3)" stroke-width="0.4" stroke-linecap="round"/>' +
          '</svg>';
      }

      frag.appendChild(p);
    }
    el.appendChild(frag);
  }

  // ════════════════════════════════════════════════════════════════════
  // Layer 2 — Steam wisps (rising, against gravity)
  // ════════════════════════════════════════════════════════════════════
  //
  // White/translucent elongated soft blobs. Sparse on purpose — too many
  // and the screen reads foggy instead of meditative. The CSS keyframes
  // for this layer are unique (they go bottom-up rather than top-down).
  function buildSteamWisps(el, ctx) {
    var intensity = ctx.intensity;
    var n = intensity === 'lush' ? 14 : intensity === 'whisper' ? 5 : 8;

    var assets = (typeof window !== 'undefined') && window.SAYURI_TEA_GARDEN_ASSETS;
    var wispImages = (assets && assets.steam) || [];

    var rand = ctx.seed('tg-steam'.charCodeAt(0) * 131 + 13 * 7919);

    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var left = rand() * 100;
      var w = 28 + rand() * 36;          // 28–64px wide
      var h = w * (1.4 + rand() * 0.6);  // taller than wide
      var delay = -rand() * 60;
      var opacity = 0.18 + rand() * 0.18; // 0.18–0.36 — soft

      var dur = 40 + rand() * 20;        // 40–60s slow rise
      var swayDur = 8 + rand() * 5;      // 8–13s lateral wobble
      var fallVariant = i % 3;

      var p = document.createElement('div');
      p.className = 'sayuri-tg-steam';
      p.style.position = 'absolute';
      p.style.bottom = '-' + Math.round(h) + 'px';
      p.style.left = left + 'vw';
      p.style.width = w + 'px';
      p.style.height = h + 'px';
      p.style.opacity = opacity;
      p.style.filter = 'blur(' + (4 + rand() * 6) + 'px)';
      p.style.animation =
        'tgl-rise-' + fallVariant + ' ' + dur + 's linear ' + delay + 's infinite, ' +
        'tgl-steam-sway ' + swayDur + 's ease-in-out infinite';

      if (wispImages.length > 0) {
        var url = wispImages[Math.floor(rand() * wispImages.length)];
        p.innerHTML = '<img src="' + url + '" alt="" draggable="false" ' +
          'style="width:100%;height:100%;display:block;object-fit:contain;">';
      } else {
        // Procedural SVG: vertical elongated radial-ish gradient blob.
        p.innerHTML =
          '<svg viewBox="0 0 64 96" xmlns="http://www.w3.org/2000/svg" ' +
          'style="width:100%;height:100%;display:block;">' +
            '<defs><radialGradient id="tgl-steam-' + i + '" cx="50%" cy="55%" r="55%">' +
              '<stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.95"/>' +
              '<stop offset="60%" stop-color="#FFFFFF" stop-opacity="0.5"/>' +
              '<stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>' +
            '</radialGradient></defs>' +
            '<ellipse cx="32" cy="50" rx="22" ry="40" fill="url(#tgl-steam-' + i + ')"/>' +
          '</svg>';
      }
      frag.appendChild(p);
    }
    el.appendChild(frag);
  }

  // ════════════════════════════════════════════════════════════════════
  // Layer 3 — Bamboo silhouettes (slow lateral drift)
  // ════════════════════════════════════════════════════════════════════
  //
  // Very low opacity vertical bamboo poles with a couple of leaves
  // drifting sideways across the viewport. Adds a sense of depth — like
  // a distant grove glimpsed through ground mist. Slowest motion of the
  // three layers.
  function buildBambooShadows(el, ctx) {
    var intensity = ctx.intensity;
    var n = intensity === 'lush' ? 9 : intensity === 'whisper' ? 4 : 6;

    var rand = ctx.seed('tg-bamboo'.charCodeAt(0) * 131 + 13 * 7919);
    var frag = document.createDocumentFragment();

    for (var i = 0; i < n; i++) {
      var top = rand() * 70;             // 0–70vh — keep mostly upper
      var w = 24 + rand() * 20;          // 24–44px wide
      var h = 110 + rand() * 80;         // 110–190px tall
      var delay = -rand() * 90;
      var opacity = 0.08 + rand() * 0.08; // 0.08–0.16 — silhouette feel

      var dur = 60 + rand() * 30;        // 60–90s very slow drift
      var driftVariant = i % 2;          // left→right or right→left

      var p = document.createElement('div');
      p.className = 'sayuri-tg-bamboo';
      p.style.position = 'absolute';
      p.style.top = top + 'vh';
      p.style.left = (driftVariant === 0 ? '-15vw' : '115vw');
      p.style.width = w + 'px';
      p.style.height = h + 'px';
      p.style.opacity = opacity;
      p.style.filter = 'blur(2px)';
      p.style.animation = 'tgl-bamboo-' + driftVariant + ' ' + dur + 's linear ' + delay + 's infinite';

      // Procedural bamboo silhouette: vertical dark-green pole with two
      // node bands and a couple of leaves.
      p.innerHTML =
        '<svg viewBox="0 0 44 200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" ' +
        'style="width:100%;height:100%;display:block;">' +
          // Pole
          '<rect x="18" y="0" width="8" height="200" fill="#2C4F22" rx="3"/>' +
          // Two node bands
          '<rect x="14" y="60" width="16" height="2.5" fill="#1F3818"/>' +
          '<rect x="14" y="130" width="16" height="2.5" fill="#1F3818"/>' +
          // A couple of leaves on alternating sides
          '<path d="M22,45 C 36,40 42,30 40,18 C 38,30 32,42 22,48 Z" fill="#2C4F22"/>' +
          '<path d="M22,115 C 6,112 0,102 4,90 C 8,102 12,114 22,118 Z" fill="#2C4F22"/>' +
        '</svg>';
      frag.appendChild(p);
    }
    el.appendChild(frag);
  }

  // ════════════════════════════════════════════════════════════════════
  // Registration
  // ════════════════════════════════════════════════════════════════════
  SA.register('tea-leaves-drift', {
    mount: function (el, ctx) {
      // Bamboo first → renders behind (z-order via DOM order).
      buildBambooShadows(el, ctx);
      buildSteamWisps(el, ctx);
      buildTeaLeaves(el, ctx);
    },
    css: [
      // ── Tea-leaf motion (slower than maple, gentler arcs) ──
      '@keyframes tgl-fall-0 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(40px,110vh,0);} }',
      '@keyframes tgl-fall-1 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(-60px,110vh,0);} }',
      '@keyframes tgl-fall-2 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(20px,110vh,0);} }',
      '@keyframes tgl-spin-0 { from{transform:rotate(0);} to{transform:rotate(360deg);} }',
      '@keyframes tgl-spin-1 { from{transform:rotate(0);} to{transform:rotate(-360deg);} }',
      '@keyframes tgl-sway   { 0%,100%{margin-left:0;} 50%{margin-left:30px;} }',
      // ── Steam motion (rising, slight horizontal drift, opacity in/out) ──
      '@keyframes tgl-rise-0 { 0%{transform:translate3d(0,0,0);opacity:0;} 12%{opacity:1;} 88%{opacity:1;} 100%{transform:translate3d(20px,-115vh,0);opacity:0;} }',
      '@keyframes tgl-rise-1 { 0%{transform:translate3d(0,0,0);opacity:0;} 14%{opacity:1;} 86%{opacity:1;} 100%{transform:translate3d(-30px,-115vh,0);opacity:0;} }',
      '@keyframes tgl-rise-2 { 0%{transform:translate3d(0,0,0);opacity:0;} 12%{opacity:1;} 88%{opacity:1;} 100%{transform:translate3d(10px,-115vh,0);opacity:0;} }',
      '@keyframes tgl-steam-sway { 0%,100%{margin-left:0;} 50%{margin-left:24px;} }',
      // ── Bamboo lateral drift ──
      '@keyframes tgl-bamboo-0 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(135vw,0,0);} }',
      '@keyframes tgl-bamboo-1 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(-135vw,0,0);} }'
    ].join('')
  });
})();
